import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TeammateAvatar} from '../../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {buildContactDisplayName} from '../../../../../helpers/models/contactHelpers';
import {VisualContactModel} from '../../../../../models/api/visualContactModel';
import {TeammateAvatarStack} from '../../../../../settings/rules/loadBalancing/teammateAvatarStackCell/teammateAvatarStack';
import {ContactAvatar} from '../../../../common/avatars/contactAvatar';
import {OverflowWithCount} from '../../../../common/overflow/overflowWithCount';
import {ContactCardPreviewer} from '../../../../contactCard/contactCardPreviewer';
import {TeammateCardPreviewer, TeammateNamePreviewer} from '../../../../teammateCard/teammateCardPreviewer';
import {SynopsisTimestamp} from './synopsisTimestamp';

/*
 * Types.
 */

// Display, by order of preference: teammates, visualContact, aiSubject (fallback to AI model).
interface SynopsisCellHeaderProps {
  teammates?: ReadonlyArray<TeammateModel>;
  visualContact?: VisualContactModel;
  timestamp: number;
  aiSubject?: string;
  panelId: ViewerPanelId;
}

/*
 * Styles.
 */

const StyledHeader = styled.header`
  display: grid;
  grid-template-columns: auto minmax(0, auto) 1fr auto;
  grid-template-areas: 'avatar author . timestamp';
  grid-gap: 8px;
  align-items: center;
  padding: 8px 12px 4px 12px;
`;

const StyledAvatarDiv = styled.div`
  grid-area: avatar;
  display: flex;
`;

const StyledAuthorDiv = styled.div`
  grid-area: author;
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};
`;

const StyledTimestampDiv = styled.div`
  grid-area: timestamp;
`;

const StyledInitialAvatarDiv = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 8px;
  background-color: ${(p) => p.theme.greys.shade60};
  color: ${(p) => p.theme.greys.shade20};
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.bold};
  text-align: center;
  line-height: 16px;
`;

const StyledInteractiveSpan = styled.span`
  &:hover {
    color: ${(p) => p.theme.interactiveTextHover};
  }
`;

const StyledTeammateNamePreviewer = styled(TeammateNamePreviewer)`
  display: inline-block;
`;

/*
 * Component.
 */

export const SynopsisCellHeader: FC<SynopsisCellHeaderProps> = ({
  teammates,
  visualContact,
  aiSubject,
  timestamp,
  panelId,
}) => {
  // For comments and messages sent by teammates.
  if (teammates && teammates.length > 0) {
    return renderTeammates(teammates, timestamp, panelId);
  }

  // For messages sent by a contact.
  if (visualContact) {
    return renderContact(visualContact, timestamp, panelId);
  }

  // Fallback on data provided by the model. This shouldn't happen.
  if (aiSubject) {
    return renderAiSubject(aiSubject, timestamp);
  }

  return null;
};

function renderTeammates(teammates: ReadonlyArray<TeammateModel>, timestamp: number, panelId: ViewerPanelId) {
  const separator =
    teammates.length === 2 ? (
      <span>
        &nbsp;<Trans id="synopsis-separator-and">and</Trans>&nbsp;
      </span>
    ) : (
      <span>,&nbsp;</span>
    );

  const avatar =
    teammates.length === 1 ? (
      <TeammateCardPreviewer teammate={teammates[0]} panelId={panelId}>
        <TeammateAvatar teammate={teammates[0]} size={VisualSizesEnum.SMALL} supportsHover />
      </TeammateCardPreviewer>
    ) : (
      <UserThemeConsumer>
        {(theme) => (
          <TeammateAvatarStack
            teammates={teammates}
            size={VisualSizesEnum.SMALL}
            borderColor={theme.message.readCommentBackground}
          />
        )}
      </UserThemeConsumer>
    );

  const authorNames = (
    <OverflowWithCount
      elements={teammates}
      renderElement={(teammate) => (
        <StyledTeammateNamePreviewer teammate={teammate} panelId={panelId} shouldDisplayFullName />
      )}
      renderOverflowIndicator={(number) => <span>&nbsp;+{number}</span>}
      renderSeparator={() => separator}
      shouldUseMaximumWidth
    />
  );

  return (
    <StyledHeader>
      <StyledAvatarDiv>{avatar}</StyledAvatarDiv>
      <StyledAuthorDiv>{authorNames}</StyledAuthorDiv>
      <StyledTimestampDiv>
        <SynopsisTimestamp timestamp={timestamp} />
      </StyledTimestampDiv>
    </StyledHeader>
  );
}

function renderContact(visualContact: VisualContactModel, timestamp: number, panelId: ViewerPanelId) {
  return (
    <StyledHeader>
      <StyledAvatarDiv>
        <ContactCardPreviewer visualContact={visualContact} panelId={panelId}>
          <ContactAvatar visualContact={visualContact} size={VisualSizesEnum.SMALL} supportsHover />
        </ContactCardPreviewer>
      </StyledAvatarDiv>
      <StyledAuthorDiv>
        <ContactCardPreviewer visualContact={visualContact} panelId={panelId}>
          <StyledInteractiveSpan>{buildContactDisplayName(visualContact)}</StyledInteractiveSpan>
        </ContactCardPreviewer>
      </StyledAuthorDiv>
      <StyledTimestampDiv>
        <SynopsisTimestamp timestamp={timestamp} />
      </StyledTimestampDiv>
    </StyledHeader>
  );
}

function renderAiSubject(aiSubject: string, timestamp: number) {
  const initial = aiSubject[0];

  return (
    <StyledHeader>
      <StyledAvatarDiv>
        <StyledInitialAvatarDiv>{initial}</StyledInitialAvatarDiv>
      </StyledAvatarDiv>
      <StyledAuthorDiv>{aiSubject}</StyledAuthorDiv>
      <StyledTimestampDiv>
        <SynopsisTimestamp timestamp={timestamp} />
      </StyledTimestampDiv>
    </StyledHeader>
  );
}
